import { GridColDef } from '@mui/x-data-grid-premium';
import { useMyAssortmentTranslation } from '@retail/my-assortment/i18n';
import { useMemo } from 'react';
import { StackedTextCell, TopLineTypography } from '@retail/components';
import { FilterCellAction } from '../FilterCellAction';
import { getDiscontinuationStatus } from '@retail/products/utils';
import { AssortmentItemBase } from '@retail/my-assortment/types';
import { ExternalItemSource, useMgItemAndExternalNumberColumn } from '@retail/products/components';
import { getLLDate } from '@retail/utils';
import { Stack, Tooltip, Typography } from '@mui/material';
import { StarBorder } from '@mui/icons-material';
import { AssortmentItemStatus } from '../AssortmentItemStatus';

interface Props {
  selectSupplier?: (supplierId: number) => void;
  selectProductGroup?: (groupNumber: string) => void;
  selectDiscountGroup?: (discountGroupCode: string, supplierId: number) => void;
  externalItemSource: ExternalItemSource;
}

const baseColumnProps: Partial<GridColDef> = {
  width: 140,
  headerName: '',
  headerAlign: 'left',
  align: 'left',
};

export const useItemInfoColumns = <T extends AssortmentItemBase>({
  selectSupplier,
  selectProductGroup,
  selectDiscountGroup,
  externalItemSource,
}: Props) => {
  const { t } = useMyAssortmentTranslation();
  const mgItemAndExternalNumberColumn = useMgItemAndExternalNumberColumn<T>({
    externalItemSource,
    columnProps: baseColumnProps,
  });

  return useMemo(() => {
    const itemIdsColumn = mgItemAndExternalNumberColumn;
    const itemNameColumn: GridColDef<T> = {
      ...baseColumnProps,
      field: 'itemText',
      headerName: t('myAssortment.columns.itemText'),
      width: 270,
      renderCell: ({ row }) => (
        <StackedTextCell
          nullableBottomLine
          topLine={row.primaryText}
          bottomLine={row.secondaryText}
        />
      ),
      valueGetter: (_, row) => row.primaryText,
    };
    const supplierColumn: GridColDef<T> = {
      ...baseColumnProps,
      width: 200,
      field: 'supplier',
      headerName: t('myAssortment.columns.supplier'),
      renderCell: ({ row }) => (
        <StackedTextCell
          topLine={
            <TopLineTypography variant="subtitle2" isInline>
              {row.supplier.name}
              {selectSupplier && (
                <FilterCellAction
                  onFilterClick={() => selectSupplier(row.supplier.id)}
                  filterTitle={t('myAssortment.actions.selectSupplierAsQuickFilter')}
                />
              )}
            </TopLineTypography>
          }
          bottomLine={row.supplier.id}
        />
      ),
      valueGetter: (_, row) => row.supplier.name,
    };
    const productGroupColumn: GridColDef<T> = {
      ...baseColumnProps,
      width: 200,
      field: 'productGroup',
      headerName: t('myAssortment.columns.itemGroup'),
      renderCell: ({ row }) => (
        <StackedTextCell
          topLine={
            <TopLineTypography variant="subtitle2" isInline>
              {row.productGroup.groupNumber}

              {row.productGroup.groupNumber && selectProductGroup && (
                <FilterCellAction
                  onFilterClick={() => selectProductGroup(row.productGroup.groupNumber)}
                  filterTitle={t('myAssortment.actions.selectProductGroupAsQuickFilter')}
                />
              )}
            </TopLineTypography>
          }
          bottomLine={row.productGroup.name}
        />
      ),
      valueGetter: (_, row) => row.productGroup.groupNumber,
    };
    const discountGroupColumn: GridColDef<T> = {
      ...baseColumnProps,
      width: 200,
      field: 'discountGroup',
      headerName: t('myAssortment.columns.discountGroup'),
      renderCell: ({ row }) => (
        <StackedTextCell
          topLine={
            <TopLineTypography variant="subtitle2" isInline>
              {row.discountGroup?.name}
              {row.discountGroup && selectDiscountGroup && (
                <FilterCellAction
                  onFilterClick={() =>
                    selectDiscountGroup(row.discountGroup!.code, row.supplier.id)
                  }
                  filterTitle={t('myAssortment.actions.selectDiscountGroupAsQuickFilter')}
                />
              )}
            </TopLineTypography>
          }
          bottomLine={row.discountGroup?.code}
        />
      ),
      valueGetter: (_, row) => row.discountGroup?.code,
    };
    const itemStatusColumn: GridColDef<T> = {
      ...baseColumnProps,
      field: 'status',
      headerAlign: 'center',
      align: 'center',
      minWidth: 20,
      width: 20,
      headerName: '',
      cellClassName: undefined,
      renderCell: ({ row }) => (
        <AssortmentItemStatus
          inAssortment={row.inAssortment}
          priorityValidFrom={row.priority.validFrom}
          priorityValidTo={row.priority.validTo}
        />
      ),
    };
    const isStockedColumn: GridColDef<T> = {
      ...baseColumnProps,
      field: 'stockStatus',
      headerAlign: 'center',
      align: 'center',
      width: 120,
      headerName: t('myAssortment.stockStatus.status'),
      renderCell: ({ row }) => (
        <Typography variant="subtitle2">
          {row.isStocked === false
            ? t('myAssortment.stockStatus.order')
            : t('myAssortment.stockStatus.inStock')}
        </Typography>
      ),
    };
    const supplierItemPriorityColumn: GridColDef<T> = {
      ...baseColumnProps,
      field: 'supplierItemPriority',
      headerAlign: 'center',
      minWidth: 30,
      width: 30,
      headerName: '',
      renderCell: ({ row }) =>
        row.priority.prioritised && (
          <Tooltip title={t('myAssortment.priority.mainPrioSupplier')} placement="top" arrow>
            <StarBorder color="success" fontSize="small" />
          </Tooltip>
        ),
    };
    const discontinuationColumn: GridColDef<T> = {
      ...baseColumnProps,
      width: 150,
      field: 'discontinuation',
      headerName: t('myAssortment.columns.discontinuation'),
      valueGetter: (_, row) => row.discontinuationStatus,
      renderCell: ({ row }) => {
        return (
          <Stack pl={1}>
            <StackedTextCell
              topLine={
                row.discontinuationStatus
                  ? getDiscontinuationStatus(row.discontinuationStatus)
                  : '-'
              }
              bottomLine={getLLDate(row.discontinuationDate)}
              nullableBottomLine
            />
          </Stack>
        );
      },
    };
    return {
      itemIdsColumn,
      itemNameColumn,
      supplierColumn,
      productGroupColumn,
      discountGroupColumn,
      itemStatusColumn,
      isStockedColumn,
      supplierItemPriorityColumn,
      discontinuationColumn,
    };
  }, [mgItemAndExternalNumberColumn, t, selectSupplier, selectProductGroup, selectDiscountGroup]);
};
